import type { PageSchemaField, PageSchemaFieldType, UiIconProps } from '@shopexperts/shared';

export interface PageFieldTypeDefinition {
  type: PageSchemaFieldType;
  label: string;
  icon: UiIconProps['name'];
  defaultValue: unknown;
  supportsOptions?: boolean;
}

export const PAGE_FIELD_TYPES: PageFieldTypeDefinition[] = [
  { type: 'text', label: 'Single line text', icon: 'pencil', defaultValue: '' },
  { type: 'textarea', label: 'Multi-line text', icon: 'file-placeholder', defaultValue: '' },
  { type: 'richtext', label: 'Rich text', icon: 'file-placeholder', defaultValue: '' },
  { type: 'number', label: 'Number', icon: 'filter', defaultValue: null },
  { type: 'boolean', label: 'Boolean', icon: 'check', defaultValue: false },
  { type: 'select', label: 'Select', icon: 'chevron-down', defaultValue: '', supportsOptions: true },
  { type: 'image', label: 'Image', icon: 'image', defaultValue: null },
  { type: 'partner_badge', label: 'Partner badge', icon: 'shopexperts-badge', defaultValue: null },
  { type: 'service_category', label: 'Service category', icon: 'category', defaultValue: null },
  { type: 'service_subcategory', label: 'Service subcategory', icon: 'category', defaultValue: null },
  { type: 'country', label: 'Country', icon: 'map-pin', defaultValue: '' },
  { type: 'experts-smart-filter', label: 'Experts smart filter', icon: 'filter', defaultValue: '' },
  { type: 'link_groups', label: 'Link groups', icon: 'link-external', defaultValue: '' },
];

export const PAGE_FIELD_TYPE_OPTIONS = PAGE_FIELD_TYPES.map((definition) => ({
  label: definition.label,
  value: definition.type,
}));

export function getPageFieldTypeDefinition(type: string): PageFieldTypeDefinition {
  return PAGE_FIELD_TYPES.find((definition) => definition.type === type) ?? PAGE_FIELD_TYPES[0];
}

export function formatMetafieldReference(key: string): string {
  return `m:${key}`;
}

export function createFieldKey(label: string, existingFields: PageSchemaField[]): string {
  const base = slugifyFieldKey(label || 'field');
  const existing = new Set(existingFields.map((field) => field.key));
  if (!existing.has(base)) return base;

  let suffix = 2;
  while (existing.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}

export function createDefaultSchemaField(type: PageSchemaFieldType, existingFields: PageSchemaField[]): PageSchemaField {
  const definition = getPageFieldTypeDefinition(type);
  const key = createFieldKey(type, existingFields);

  return {
    key,
    label: definition.label,
    type,
    required: false,
    default_value: definition.defaultValue,
    options: definition.supportsOptions ? ['Option 1'] : [],
  };
}

export function validateSchemaFields(fields: PageSchemaField[]): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();

  fields.forEach((field, index) => {
    const label = field.label || `Field ${index + 1}`;
    if (!field.key.trim()) errors.push(`${label} needs a key.`);
    if (!/^[a-z0-9_]+$/.test(field.key)) errors.push(`${label} key must use lowercase letters, numbers, and underscores only.`);
    if (seen.has(field.key)) errors.push(`${label} has a duplicate key.`);
    seen.add(field.key);
    if (!field.label.trim()) errors.push(`${field.key || `Field ${index + 1}`} needs a label.`);
    if (field.type === 'select' && (!field.options || !field.options.some((option) => option.trim()))) {
      errors.push(`${label} needs at least one select option.`);
    }
  });

  return errors;
}

export function initialValueForField(field: PageSchemaField): unknown {
  if (field.default_value !== undefined && field.default_value !== null) return field.default_value;
  return getPageFieldTypeDefinition(field.type).defaultValue;
}

function slugifyFieldKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    || 'field';
}
